import {
  FormVideoType,
  type ImageFileInput,
  PublishOptionMode,
  type VideoFileInput,
  type VideoSubmissionFormInput,
} from '@/graphql/v2';
import { PUBLISH_OPTION } from '@/utils/constants/sermons';
import { resolvePublishTimestamp } from '@/utils/sermons/sermon-date-utils';
import type { SermonUploadSchema } from '@/validation-schemas/sermon-upload-form';
import type { VideoUploadSchema } from '@/validation-schemas/video-upload-form';

/**
 * Extracts file extension without the dot.
 *
 * @param filename - The filename to extract extension from.
 * @returns The file extension without the leading dot.
 * @example
 * ```typescript
 * getFileExtension('video.mp4') // Returns: 'mp4'
 * getFileExtension('image.file.jpg') // Returns: 'jpg'
 * ```
 */
function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

/**
 * Converts a File object to ImageFileInput for GraphQL.
 *
 * @param file - The File object to convert.
 * @returns The ImageFileInput object with file metadata.
 * @example
 * ```typescript
 * const imageFile = new File(['content'], 'image.jpg', { type: 'image/jpeg' });
 * const input = fileToImageInput(imageFile);
 * // Returns: { type: 'image/jpeg', extension: 'jpg', name: 'image.jpg' }
 * ```
 */
function fileToImageInput(file: File): ImageFileInput {
  return {
    extension: getFileExtension(file.name),
    type: file.type,
    name: file.name,
  };
}

/**
 * Converts a File object to VideoFileInput for GraphQL.
 *
 * @param file - The File object to convert.
 * @returns The VideoFileInput object with file metadata.
 * @example
 * ```typescript
 * const videoFile = new File(['content'], 'video.mp4', { type: 'video/mp4' });
 * const input = fileToVideoInput(videoFile);
 * // Returns: { type: 'video/mp4', extension: 'mp4', name: 'video.mp4' }
 * ```
 */
function fileToVideoInput(file: File): VideoFileInput {
  return {
    extension: getFileExtension(file.name),
    type: file.type,
    name: file.name,
  };
}

/**
 * Result of transforming video form values for submission.
 * Contains both the GraphQL input (metadata) and the File blobs for GCS upload.
 */
export type VideoSubmissionData = {
  /** File blobs to be uploaded to GCS using presigned URLs. */
  files: {
    thumbnail: File;
    video: File;
  };
  /** GraphQL input passed to `post_cms_video` or `post_cms_sermon` depending on flow. */
  input: VideoSubmissionFormInput;
};

/**
 * Transforms form values for video submission.
 * Extracts file metadata for GraphQL mutation while preserving File blobs for GCS upload.
 * Flow: 1) `post_cms_video` receives `input` (metadata only) and returns CMS upload slots, 2) client uploads
 * `files` to GCS using those URLs. No OptiMS operation polling.
 *
 * @param formValues - Form values from VideoUploadSchema.
 * @throws Error if files are not provided.
 * @returns Object containing GraphQL input and File blobs.
 * @example
 * ```typescript
 * const formValues = { title: 'My Video', videoFile: new File([''], 'video.mp4'), ... };
 * const submissionData = transformToVideoSubmissionInput(formValues);
 * ```
 */
export function transformToVideoSubmissionInput(formValues: VideoUploadSchema): VideoSubmissionData {
  // At this point, validation ensures files are not null
  if (!formValues.imageThumbnail || !formValues.videoFile) {
    throw new Error('Files must be provided');
  }

  return {
    files: {
      thumbnail: formValues.imageThumbnail,
      video: formValues.videoFile,
    },
    input: {
      bibleReferences: formValues.bibleReferences?.length ? formValues.bibleReferences : undefined,
      description: formValues.description,
      imageThumbnail: fileToImageInput(formValues.imageThumbnail),
      language: formValues.language,
      organizationId: formValues.organizationId,
      // Non-sermon video uploads do not offer scheduled publishing; omit publishOption so the BFF
      // defaults to WHEN_READY.
      title: formValues.title,
      videoFile: fileToVideoInput(formValues.videoFile),
      videoType: formValues.videoType as FormVideoType,
    },
  };
}

/**
 * Transforms form values for sermon submission.
 * Appends speaker and series to description, sets videoType to SERMON.
 * Extracts file metadata for GraphQL mutation while preserving File blobs for GCS upload.
 * Flow: 1) `post_cms_sermon` receives `input` and returns CMS upload slots, 2) client uploads `files`
 * to GCS using those URLs, 3) sermon upload workflow completes without OptiMS operation polling.
 *
 * @param formValues - Form values from SermonUploadSchema.
 * @throws Error if files are not provided.
 * @returns Object containing GraphQL input and File blobs.
 * @example
 * ```typescript
 * const formValues = { title: 'My Sermon', videoFile: new File([''], 'video.mp4'), ... };
 * const submissionData = transformToSermonSubmissionInput(formValues);
 * ```
 */
export function transformToSermonSubmissionInput(formValues: SermonUploadSchema): VideoSubmissionData {
  // At this point, validation ensures files are not null
  if (!formValues.imageThumbnail || !formValues.videoFile) {
    throw new Error('Files must be provided');
  }

  const metadata = [
    formValues.speaker?.trim() && `Speaker: ${formValues.speaker.trim()}`,
    formValues.series?.trim() && `Sermon Series: ${formValues.series.trim()}`,
  ]
    .filter(Boolean)
    .join('\n');

  // Publish intent: the form collects `publishOption` (now/scheduled) + a `publishDate`. Emit the
  // nested PublishOptionInput. Only send the SCHEDULED object (with timestamp/timezone) when the
  // partner chose to schedule *and* picked a date; otherwise omit publishOption so the BFF defaults
  // to WHEN_READY.
  const scheduledDate = formValues.publishOption === PUBLISH_OPTION.SCHEDULED ? (formValues.publishDate ?? null) : null;
  const publishOption = scheduledDate
    ? {
        mode: PublishOptionMode.SCHEDULED,
        requested_publish_timestamp: scheduledDate.toISOString(),
        requested_timezone: formValues.timezone,
      }
    : undefined;

  const input: VideoSubmissionFormInput = {
    bibleReferences: formValues.bibleReferences?.length ? formValues.bibleReferences : undefined,
    description: metadata ? `${formValues.description}\n\n${metadata}` : formValues.description,
    imageThumbnail: fileToImageInput(formValues.imageThumbnail),
    language: formValues.language,
    organizationId: formValues.organizationId,
    publishOption,
    title: formValues.title,
    videoFile: fileToVideoInput(formValues.videoFile),
    videoType: FormVideoType.SERMON,
  };

  if (formValues.publishOption === PUBLISH_OPTION.SCHEDULED && formValues.publishDate) {
    input.publish_request = {
      requested_publish_timestamp: resolvePublishTimestamp(formValues.publishDate, formValues.timezone),
      requested_timezone: formValues.timezone,
    };
  }

  return {
    files: {
      thumbnail: formValues.imageThumbnail,
      video: formValues.videoFile,
    },
    input,
  };
}
